import { formatQueueTimestamp } from '../../../core/api/dto-mappers';

export type PlaybackTimeZone = {
  olson: string;
  utc?: string;
  dst?: string;
  isDstIgnored?: boolean;
};

const ISO_WITHOUT_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;
const OFFSET_PATTERN = /^([+-])(\d{2}):(\d{2})$/;

/** Parses an alarm/playback timestamp, treating missing offsets as UTC. */
export function parseUtcInstant(value: string): Date {
  const trimmed = value.trim();
  if (ISO_WITHOUT_OFFSET.test(trimmed)) {
    return new Date(`${trimmed}Z`);
  }
  return new Date(trimmed);
}

/** Resolves the IANA zone used to display playback wall-clock times. */
export function resolvePlaybackTimeZone(timeZone?: PlaybackTimeZone | null): string {
  const olson = timeZone?.olson?.trim();
  if (!olson) {
    return 'UTC';
  }
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: olson }).format(new Date());
    return olson;
  } catch {
    return 'UTC';
  }
}

/** NVR abbreviation for playback picker field labels. */
export function playbackTimeZoneAbbreviation(
  timeZone?: PlaybackTimeZone | null,
  at?: string | Date | null,
): string {
  const value =
    typeof at === 'string'
      ? at
      : at instanceof Date && !Number.isNaN(at.getTime())
        ? at.toISOString()
        : new Date().toISOString();
  const formatted = formatPlaybackTimestamp(value, timeZone);
  const abbr = formatted.trim().split(/\s+/).at(-1);
  return abbr && abbr !== formatted ? abbr : 'UTC';
}

/** Formats a UTC alarm/playback instant in the camera's NVR timezone. */
export function formatPlaybackTimestamp(
  value: string,
  timeZone?: PlaybackTimeZone | null,
): string {
  const date = parseUtcInstant(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  if (timeZone?.isDstIgnored) {
    const offset = timeZone.utc?.trim();
    if (offset && OFFSET_PATTERN.test(offset)) {
      return formatFixedOffsetTimestamp(date, offset);
    }
  }

  return formatQueueTimestamp(date.toISOString(), resolvePlaybackTimeZone(timeZone));
}

function shouldRemapPicker(timeZone?: PlaybackTimeZone | null): boolean {
  if (timeZone?.isDstIgnored && timeZone.utc?.trim() && OFFSET_PATTERN.test(timeZone.utc.trim())) {
    return true;
  }
  return resolvePlaybackTimeZone(timeZone) !== 'UTC';
}

/**
 * UTC ISO instant whose clock matches the NVR wall clock of `instant`.
 * Resolve gallery hover reads those UTC fields as the capture time
 * (`2026-10-14T22:57:35.000Z` displays as `10/14/2026 22:57:35`).
 */
export function nvrWallClockAsUtcIso(instant: Date, timeZone?: PlaybackTimeZone | null): string {
  if (Number.isNaN(instant.getTime()) || !shouldRemapPicker(timeZone)) {
    return Number.isNaN(instant.getTime()) ? new Date().toISOString() : instant.toISOString();
  }

  const wall = toNvrWallClockDate(instant, timeZone);
  if (!wall || Number.isNaN(wall.getTime())) {
    return instant.toISOString();
  }

  return new Date(
    Date.UTC(
      wall.getFullYear(),
      wall.getMonth(),
      wall.getDate(),
      wall.getHours(),
      wall.getMinutes(),
      wall.getSeconds(),
      wall.getMilliseconds(),
    ),
  ).toISOString();
}

/** Date whose local Y/M/D H:m:s matches the NVR wall clock of `instant`. */
export function toNvrWallClockDate(
  instant: Date | null,
  timeZone?: PlaybackTimeZone | null,
): Date | null {
  if (!instant || Number.isNaN(instant.getTime()) || !shouldRemapPicker(timeZone)) {
    return instant;
  }

  if (timeZone?.isDstIgnored) {
    const offset = timeZone.utc?.trim();
    if (offset && OFFSET_PATTERN.test(offset)) {
      return utcPartsAsLocalDate(new Date(instant.getTime() + offsetToMs(offset)));
    }
  }

  const parts = zonedDateParts(instant, resolvePlaybackTimeZone(timeZone));
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

/** Interprets a picker Date's local fields as NVR wall clock and returns the UTC instant. */
export function fromNvrWallClockDate(
  wall: Date,
  timeZone?: PlaybackTimeZone | null,
): Date {
  if (!shouldRemapPicker(timeZone)) {
    return wall;
  }

  const year = wall.getFullYear();
  const month = wall.getMonth();
  const day = wall.getDate();
  const hour = wall.getHours();
  const minute = wall.getMinutes();
  const second = wall.getSeconds();

  if (timeZone?.isDstIgnored) {
    const offset = timeZone.utc?.trim();
    if (offset && OFFSET_PATTERN.test(offset)) {
      return new Date(Date.UTC(year, month, day, hour, minute, second) - offsetToMs(offset));
    }
  }

  return zonedWallTimeToUtc(
    year,
    month + 1,
    day,
    hour,
    minute,
    second,
    resolvePlaybackTimeZone(timeZone),
  );
}

function formatFixedOffsetTimestamp(date: Date, offset: string): string {
  const shifted = new Date(date.getTime() + offsetToMs(offset));
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(shifted);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('month')}/${get('day')}/${get('year')} ${get('hour')}:${get('minute')}:${get('second')} UTC${offset}`;
}

function offsetToMs(offset: string): number {
  const match = offset.match(OFFSET_PATTERN);
  if (!match) {
    return 0;
  }
  const sign = match[1] === '-' ? -1 : 1;
  return sign * (Number(match[2]) * 60 + Number(match[3])) * 60_000;
}

function utcPartsAsLocalDate(date: Date): Date {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
}

type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function zonedDateParts(date: Date, timeZone: string): ZonedDateParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

function zonedWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): Date {
  const wanted = Date.UTC(year, month - 1, day, hour, minute, second);
  let guess = wanted;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const shown = zonedDateParts(new Date(guess), timeZone);
    const shownAsUtc = Date.UTC(
      shown.year,
      shown.month - 1,
      shown.day,
      shown.hour,
      shown.minute,
      shown.second,
    );
    guess += wanted - shownAsUtc;
  }
  return new Date(guess);
}
